import { AudioItem } from '../../items/audio/audio-item-type';
import { CaptionsItem } from '../../items/captions/captions-item-type';
import { GifItem } from '../../items/gif/gif-item-type';
import { EditorStarterItem } from '../../items/item-type';
import { VideoItem } from '../../items/video/video-item-type';
import { WebpItem } from '../../items/webp/webp-item-type';
import {
  AudioFadableItem,
  getCanFadeAudio,
  getCanFadeVisual,
  VisuallyFadableItem,
} from '../../utils/fade';
import { generateRandomId } from '../../utils/generate-random-id';
import { EditorState, TrackType } from '../types';
import { itemOwner, splitGroup } from '@waka/compat/group-split';
import {preserveItemAnimationWindow} from '@waka/compat/animation-window';
import {advanceNativeCallLaneBar} from '@waka/compat/call-lane-bar';

export const splitItem = ({
  state,
  idToSplit,
  framePosition,
}: {
  state: EditorState;
  idToSplit: string;
  framePosition: number;
}): EditorState => {
  // 분할할 track과 item 찾기
  let targetTrack: TrackType | undefined;
  const targetItem: EditorStarterItem | undefined =
    state.undoableState.items[idToSplit];
  let targetTrackIndex = -1;

  for (let i = 0; i < state.undoableState.tracks.length; i++) {
    const track = state.undoableState.tracks[i];
    if (!track) continue;
    if (track.items.includes(idToSplit)) {
      targetTrack = track;
      targetTrackIndex = i;
      break;
    }
  }

  const owner = itemOwner(state, idToSplit);
  if (!targetTrack && owner) {
    targetTrack = owner.track;
    targetTrackIndex = state.undoableState.tracks.findIndex(
      (track) => track.id === owner.track.id,
    );
  }
  if (
    !targetTrack ||
    targetTrack.locked ||
    !targetItem ||
    targetTrackIndex === -1 ||
    !Number.isInteger(framePosition) ||
    !Number.isFinite(state.undoableState.fps) ||
    state.undoableState.fps <= 0
  ) {
    return state;
  }

  // item 내에서 상대적 분할 위치 계산
  const itemStart = targetItem.from;
  const itemEnd = itemStart + targetItem.durationInFrames;

  // frame 위치가 item 범위 내에 있고 가장자리가 아니면 분할
  if (framePosition <= itemStart || framePosition >= itemEnd) {
    return state;
  }
  if (targetItem.type === 'group')
    return splitGroup(state, idToSplit, framePosition, splitItem);

  // 두 개의 새로운 item 생성
  const firstItemDuration = framePosition - itemStart;
  const secondItemDuration = itemEnd - framePosition;
  const allocated = new Set([
    ...Object.keys(state.undoableState.items),
    ...Object.keys(state.undoableState.assets),
    ...Object.keys(state.undoableState.transitions),
  ]);
  const allocateId = (prefix = '') => {
    const base = prefix + generateRandomId(12);
    let id = base,
      index = 1;
    while (allocated.has(id)) id = base + '-' + index++;
    allocated.add(id);
    return id;
  };

  const firstItem: EditorStarterItem = {
    ...preserveItemAnimationWindow(targetItem,state.undoableState.fps),
    timelineSourceItemId: targetItem.timelineSourceItemId ?? targetItem.id,
    id: allocateId(),
    durationInFrames: firstItemDuration,
  };

  const secondItem: EditorStarterItem = {
    ...advanceNativeCallLaneBar(preserveItemAnimationWindow(targetItem,state.undoableState.fps,firstItemDuration),firstItemDuration),
    timelineSourceItemId: targetItem.timelineSourceItemId ?? targetItem.id,
    id: allocateId(),
    from: framePosition,
    durationInFrames: secondItemDuration,
  };

  // video의 특별한 경우 처리 - 두 번째 item의 videoStartFromInSeconds 조정
  if (targetItem.type === 'video') {
    const firstItemDurationInSeconds =
      firstItemDuration / state.undoableState.fps;
    (secondItem as VideoItem).videoStartFromInSeconds =
      (targetItem.videoStartFromInSeconds || 0) +
      firstItemDurationInSeconds * targetItem.playbackRate;
  }

  // audio의 특별한 경우 처리 - 두 번째 item의 audioStartFromInSeconds 조정
  if (targetItem.type === 'audio') {
    const firstItemDurationInSeconds =
      firstItemDuration / state.undoableState.fps;
    (secondItem as AudioItem).audioStartFromInSeconds =
      (targetItem.audioStartFromInSeconds || 0) +
      firstItemDurationInSeconds * targetItem.playbackRate;
  }

  // gif의 특별한 경우 처리 - 두 번째 item의 gifStartFromInSeconds 조정
  if (targetItem.type === 'gif') {
    const firstItemDurationInSeconds =
      firstItemDuration / state.undoableState.fps;
    (secondItem as GifItem).gifStartFromInSeconds =
      (targetItem.gifStartFromInSeconds || 0) +
      firstItemDurationInSeconds * targetItem.playbackRate;
  }

  // webp의 특별한 경우 처리 - 두 번째 item의 webpStartFromInSeconds 조정
  if (targetItem.type === 'webp') {
    const firstItemDurationInSeconds =
      firstItemDuration / state.undoableState.fps;
    (secondItem as WebpItem).webpStartFromInSeconds =
      (targetItem.webpStartFromInSeconds || 0) +
      firstItemDurationInSeconds * targetItem.playbackRate;
  }

  // captions의 특별한 경우 처리 - 두 번째 item의 captionStartInSeconds 조정
  if (targetItem.type === 'captions') {
    const firstItemDurationInSeconds =
      firstItemDuration / state.undoableState.fps;
    (secondItem as CaptionsItem).captionStartInSeconds =
      (targetItem.captionStartInSeconds || 0) + firstItemDurationInSeconds;
  }

  // Keep authored motion curves, including off-clip handles that can be revealed by extending again.
  if ('positionKeyframes' in targetItem && 'positionKeyframes' in secondItem) {
    secondItem.positionKeyframes = targetItem.positionKeyframes?.map(
      (point) => ({ ...point, frame: point.frame - firstItemDuration }),
    );
  }
  if ('zoomKeyframes' in targetItem && 'zoomKeyframes' in secondItem) {
    secondItem.zoomKeyframes = targetItem.zoomKeyframes?.map((point) => ({
      ...point,
      frame: point.frame - firstItemDuration,
    }));
  }
  const newAssets = { ...state.undoableState.assets };
  if (
    targetItem.type === 'captions' &&
    firstItem.type === 'captions' &&
    secondItem.type === 'captions'
  ) {
    const asset = newAssets[targetItem.assetId];
    if (asset?.type !== 'caption') return state;
    for (const piece of [firstItem, secondItem]) {
      const assetId = allocateId('caption-');
      newAssets[assetId] = {
        ...asset,
        timelineSourceAssetId: asset.timelineSourceAssetId ?? asset.id,
        id: assetId,
        filename: `${assetId}.json`,
        remoteUrl: null,
        remoteFileKey: null,
        captions: asset.captions.map((token) => ({ ...token })),
      };
      piece.assetId = assetId;
    }
  }

  // fadable item의 특별한 경우 - 첫 번째 item은 fadein 유지, 두 번째 item은 fade out 유지
  if (getCanFadeVisual(targetItem) && !firstItem.visualFadeWindow) {
    (firstItem as VisuallyFadableItem).fadeOutDurationInSeconds = 0;
    (secondItem as VisuallyFadableItem).fadeInDurationInSeconds = 0;
    (firstItem as VisuallyFadableItem).fadeOutDurationInSeconds = Math.min(
      (firstItem as VisuallyFadableItem).fadeOutDurationInSeconds,
      firstItemDuration / state.undoableState.fps,
    );
    (secondItem as VisuallyFadableItem).fadeOutDurationInSeconds = Math.min(
      (secondItem as VisuallyFadableItem).fadeOutDurationInSeconds,
      secondItemDuration / state.undoableState.fps,
    );
  }

  // audio와 video의 특별한 경우 - 첫 번째 item은 volume fade in 유지, 두 번째 item은 fade out 유지
  if (getCanFadeAudio(targetItem) && !firstItem.audioFadeWindow) {
    (firstItem as AudioFadableItem).audioFadeOutDurationInSeconds = 0;
    (secondItem as AudioFadableItem).audioFadeInDurationInSeconds = 0;
    (firstItem as AudioFadableItem).audioFadeOutDurationInSeconds = Math.min(
      (firstItem as AudioFadableItem).audioFadeOutDurationInSeconds,
      firstItemDuration / state.undoableState.fps,
    );
    (secondItem as AudioFadableItem).audioFadeOutDurationInSeconds = Math.min(
      (secondItem as AudioFadableItem).audioFadeOutDurationInSeconds,
      secondItemDuration / state.undoableState.fps,
    );
  }

  // 분할된 item들이 있는 업데이트된 track들 반환
  const newTracks = state.undoableState.tracks.map(
    (track, index): TrackType => {
      if (index !== targetTrackIndex) {
        return track;
      }

      return {
        ...track,
        items: track.items.flatMap((id) =>
          id === idToSplit ? [firstItem.id, secondItem.id] : [id],
        ),
      };
    },
  );

  const newItems = {
    ...state.undoableState.items,
    [firstItem.id]: firstItem,
    [secondItem.id]: secondItem,
  };

  delete newItems[idToSplit];
  const parent = owner?.parentId ? newItems[owner.parentId] : null;
  if (parent?.type === 'group')
    newItems[parent.id] = {
      ...parent,
      children: parent.children.flatMap((id) =>
        id === idToSplit ? [firstItem.id, secondItem.id] : [id],
      ),
    };

  return {
    ...state,
    undoableState: {
      ...state.undoableState,
      tracks: newTracks,
      items: newItems,
      assets: newAssets,
      transitions: Object.fromEntries(
        Object.entries(state.undoableState.transitions).map(
          ([id, transition]) => [
            id,
            {
              ...transition,
              sourceItemId:
                transition.sourceItemId === idToSplit
                  ? secondItem.id
                  : transition.sourceItemId,
              targetItemId:
                transition.targetItemId === idToSplit
                  ? firstItem.id
                  : transition.targetItemId,
            },
          ],
        ),
      ),
    },
    selectedItems: [secondItem.id],
  };
};
